"use client";
import { forwardRef } from "@chakra-ui/react";
import type { FC, ReactElement, ReactNode, ReactPortal, Ref, JSX } from "react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { H } from "../../content/heading/A11yHeading";
import { IconButton } from "../../forms/icon-button/IconButton";
import type { BoxProps } from "../../layout/box/Box";
import { Box } from "../../layout/box/Box";
import type { FlexProps } from "../../layout/flex/Flex";
import { Flex } from "../../layout/flex/Flex";
import { styles } from "./CarouselStyles";
import { useIsomorphicLayoutEffect } from "../../../hooks/useIsomorphicLayoutEffect";
import { useNavigationControls } from "./hooks/useNavigationControls";
import { useSliderNodeHandler } from "./hooks/useSliderNodeHandler";
import { useSlidesPerView } from "./hooks/useSlidesPerView";
import { ChevronLeftIcon, ChevronRightIcon } from "./Icons";

/* Swiper Carousel imports only for Hero Carousel*/
import type { SwiperProps } from "swiper/react";

type CarouselProps = BoxProps & {
    extraNavigationButtons?: ReactNode;
    showNavigation?: boolean;
    titleAriaLabel?: string;
    slidesPerView?: number | number[];
    gap?: number;
    justifySlider?: string | string[];
    hasTimer?: boolean;
    delay?: number;
    newActiveSlide?: number;
    navsx?: {
        rightButton?: object;
        leftButton?: object;
        flexContainer?: object;
    };
    theme?: string;
    /** When true, slides size from content/tile width instead of computed minWidth. */
    disableItemMinWidth?: boolean;
};
type CarouselSliderProps = FlexProps & {
    gap?: number;
};
type CarouselItemProps = FlexProps & {
    itemWidth: number;
    disableItemMinWidth?: boolean;
    ref?: Ref<HTMLDivElement>;
    index?: number;
    setActiveSlide?: (index: number) => void;
    setActiveTheme?: (theme: string) => void;
};
type CarouselNavigationProps = FlexProps &
    SwiperProps & {
        arrayChildren: (ReactElement<any> | number | string | Iterable<ReactNode> | ReactPortal)[];
        sliderNode: HTMLDivElement;
        itemWidth: number;
        nextSlideTitle?: string;
        previousSlideTitle?: string;
        navsx?: {
            rightButton?: object;
            leftButton?: object;
            flexContainer?: object;
        };
    };

const DEFAULT_GAP = 8;
const DEFAULT_TITLE = "";
export const DEFAULT_SLIDES_PER_VIEW = 1.5;
const DEFAULT_SHOW_NAVIGATION = false;
const DEFAULT_TITLE_ARIA_LABEL = "Carousel Title";
const DEFAULT_EXTRA_NAVIGATION_BUTTONS = null;
const DEFAULT_JUSTIFY_SLIDER = "flex-start";

export const Carousel: FC<CarouselProps> = ({
    h,
    sx,
    navsx,
    gap = DEFAULT_GAP,
    title = DEFAULT_TITLE,
    height,
    children,
    showNavigation = DEFAULT_SHOW_NAVIGATION,
    titleAriaLabel = DEFAULT_TITLE_ARIA_LABEL,
    slidesPerView: _slidesPerView = DEFAULT_SLIDES_PER_VIEW,
    extraNavigationButtons = DEFAULT_EXTRA_NAVIGATION_BUTTONS,
    justifySlider = DEFAULT_JUSTIFY_SLIDER,
    newActiveSlide,
    theme,
    disableItemMinWidth = false,
    ...rest
}): JSX.Element => {
    const { slidesPerView } = useSlidesPerView(_slidesPerView);
    const arrayChildren = (
        Array.isArray(children) ? children : children === null ? [] : [children]
    ) as ReactElement[];
    const { sliderRef, itemWidth, sliderNode } = useSliderNodeHandler<HTMLDivElement>(
        slidesPerView,
        gap,
        arrayChildren
    );

    const themeSurface = useMemo(
        () => (styles.theme as Record<string, Record<string, any>>)[theme as string]?.surface,
        [theme]
    );
    const themeText = useMemo(
        () => (styles.theme as Record<string, Record<string, any>>)[theme as string]?.text,
        [theme]
    );

    const amountOfSlides = arrayChildren.length;
    const showNavigationControls = showNavigation && amountOfSlides > slidesPerView;
    const showHeader = title || showNavigationControls || extraNavigationButtons;

    const { scrollToSlide } = useNavigationControls(sliderNode, itemWidth, arrayChildren);
    const carouselNavigationProps = {
        itemWidth,
        sliderNode,
        arrayChildren,
    };

    const carouselSliderProps = {
        gap,
    };

    const carouselItemProps = {
        h,
        height,
        itemWidth,
        disableItemMinWidth,
    };

    useIsomorphicLayoutEffect(() => {
        if (itemWidth > 0 && newActiveSlide !== undefined) {
            // Need to wait until render is done to make an update of active element and do scroll
            const timeoutId = setTimeout(() => {
                scrollToSlide(newActiveSlide);
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [newActiveSlide, scrollToSlide, itemWidth]);

    return (
        <Box sx={{ ...styles.carousel, ...themeSurface, ...sx }} {...rest}>
            {showHeader && (
                <Flex justifyContent={title ? "space-between" : "flex-end"} alignItems="center">
                    {title && (
                        <H
                            as={"h5"}
                            fontSize={["mobileH5", "tabletH5", "desktopH5"]}
                            /* In order to get the transition on the scrollbar, we need overwrite this value with important to avoid hide the headline. TBD by Designers. */
                            color="black !important"
                            aria-label={titleAriaLabel}
                            textTransform={"uppercase"}
                            {...themeText}
                        >
                            {title}
                        </H>
                    )}
                    <Flex gap={2} color="black">
                        {extraNavigationButtons}
                        {showNavigationControls && (
                            <CarouselNavigation {...carouselNavigationProps} />
                        )}
                    </Flex>
                </Flex>
            )}
            <Box w="100%">
                <CarouselSlider
                    ref={sliderRef}
                    mt={showHeader ? 3 : "unset"}
                    justifyContent={justifySlider}
                    /* The spacing on the last slide on mobile should be the same as in the gap */
                    paddingRight={[`${gap}px`, `${gap}px`, 0]}
                    {...carouselSliderProps}
                >
                    {arrayChildren.map((child) => (
                        <CarouselItem key={child.key ?? "carousel-item"} {...carouselItemProps}>
                            {child}
                        </CarouselItem>
                    ))}
                </CarouselSlider>
            </Box>
        </Box>
    );
};

export const CarouselSlider = forwardRef<CarouselSliderProps, "div">(
    ({ sx, gap, children, ...rest }, ref): JSX.Element => {
        const isDraggingRef = useRef(false);
        const dragStateRef = useRef({ startX: 0, startScrollLeft: 0, moved: false });
        const dragElRef = useRef<any>(null);

        // Keep exact listener references so we can reliably remove them on unmount
        const moveListenerRef = useRef<any>(null);
        const upListenerRef = useRef<any>(null);
        const cancelListenerRef = useRef<any>(null);
        const [dragging, setDragging] = useState(false);

        // Ensure global listeners are removed if the component unmounts mid-drag
        useEffect(() => {
            return () => {
                if (typeof window !== "undefined") {
                    if (moveListenerRef.current) {
                        window.removeEventListener("pointermove", moveListenerRef.current);
                    }
                    if (upListenerRef.current) {
                        window.removeEventListener("pointerup", upListenerRef.current);
                    }
                    if (cancelListenerRef.current) {
                        window.removeEventListener("pointercancel", cancelListenerRef.current);
                    }
                }
                moveListenerRef.current = null;
                upListenerRef.current = null;
                cancelListenerRef.current = null;
                dragElRef.current = null;
                isDraggingRef.current = false;
                // Make sure no residual click suppression remains
                dragStateRef.current.moved = false;
            };
        }, []);

        const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
            // Enable drag-to-scroll only for mouse; let touch/pen use native scrolling
            if (event.pointerType !== "mouse" || event.button !== 0) return;
            const el = event.currentTarget;
            // Do NOT preventDefault here to allow normal clicks on tiles/links
            dragElRef.current = el;
            isDraggingRef.current = true;
            setDragging(true);
            dragStateRef.current = {
                startX: event.clientX,
                startScrollLeft: el.scrollLeft,
                moved: false,
            };
            // Attach window listeners to keep dragging even if pointer leaves the element
            moveListenerRef.current = onPointerMove;
            upListenerRef.current = endDrag;
            cancelListenerRef.current = endDrag;
            window.addEventListener("pointermove", moveListenerRef.current, { passive: false });
            window.addEventListener("pointerup", upListenerRef.current, { passive: true });
            window.addEventListener("pointercancel", cancelListenerRef.current, { passive: true });
        };

        const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
            if (!isDraggingRef.current) return;
            const el =
                event.currentTarget && event.currentTarget.scrollLeft !== undefined
                    ? event.currentTarget
                    : dragElRef.current;
            if (!el) return;
            const { startX, startScrollLeft, moved } = dragStateRef.current;
            const dx = event.clientX - startX;
            if (!moved && Math.abs(dx) > 5) {
                dragStateRef.current.moved = true;
            }
            // Once dragging is confirmed, prevent native text selection/dragging
            if (dragStateRef.current.moved) {
                event.preventDefault?.();
            }
            // Invert dx to scroll the content in the drag direction
            el.scrollTo({ left: startScrollLeft - dx, behavior: "instant" });
        };

        const endDrag = () => {
            if (!isDraggingRef.current) return;
            isDraggingRef.current = false;
            setDragging(false);

            // Cleanup window listeners
            if (moveListenerRef.current) {
                window.removeEventListener("pointermove", moveListenerRef.current);
            }
            if (upListenerRef.current) {
                window.removeEventListener("pointerup", upListenerRef.current);
            }
            if (cancelListenerRef.current) {
                window.removeEventListener("pointercancel", cancelListenerRef.current);
            }
            moveListenerRef.current = null;
            upListenerRef.current = null;
            cancelListenerRef.current = null;
            dragElRef.current = null;

            // Ensure we don't permanently suppress the next click if no click event fires
            // Reset the moved flag on the next tick (after potential click event)
            setTimeout(() => {
                dragStateRef.current.moved = false;
            }, 0);
        };

        const onClickCapture = (event: React.MouseEvent<HTMLElement>) => {
            // Prevent accidental clicks when a drag occurred
            if (dragStateRef.current.moved) {
                event.preventDefault?.();
                event.stopPropagation?.();
            }
            dragStateRef.current.moved = false;
        };

        const baseStyles = { ...styles.carouselSlider, ...sx } as FlexProps["sx"];

        return (
            <Flex
                sx={{
                    ...baseStyles,
                    cursor: dragging ? "grabbing" : baseStyles?.cursor || "grab",
                    userSelect: dragging ? "none" : baseStyles?.userSelect,
                    touchAction: baseStyles?.touchAction || "pan-x pan-y",
                    scrollSnapType: dragging ? "none" : baseStyles?.scrollSnapType,
                }}
                gap={gap !== undefined ? `${gap}px` : undefined}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onDragStart={(e) => {
                    // Avoid native drag (images/links) hijacking our pointer drag
                    e.preventDefault?.();
                }}
                onClickCapture={onClickCapture}
                {...rest}
                ref={ref}
            >
                {children}
            </Flex>
        );
    }
);

CarouselSlider.displayName = "CarouselSlider";

const CarouselItem: FC<CarouselItemProps> = ({
    children,
    itemWidth,
    disableItemMinWidth,
    ...rest
}): JSX.Element => {
    return (
        <Flex {...rest} {...(!disableItemMinWidth ? { minW: `${itemWidth}px` } : undefined)}>
            {children}
        </Flex>
    );
};

const CarouselNavigation: FC<CarouselNavigationProps> = ({
    itemWidth,
    sliderNode,
    arrayChildren,
    nextSlideTitle = "",
    previousSlideTitle = "",
    ...rest
}): JSX.Element => {
    const { isNextDisabled, isPreviousDisabled, scrollToNextSlide, scrollToPrevSlide } =
        useNavigationControls(sliderNode, itemWidth, arrayChildren);

    return (
        <Flex display={["none", "flex"]} gap={2} sx={{ ...styles.carouselNavigation }} {...rest}>
            <IconButton
                onClick={scrollToPrevSlide}
                isRound
                variant={"tertiary"}
                aria-label={previousSlideTitle}
                w={10}
                h={10}
                disabled={isPreviousDisabled}
                data-testid="carousel-previous-button"
            >
                <ChevronLeftIcon />
            </IconButton>
            <IconButton
                onClick={scrollToNextSlide}
                isRound
                variant={"tertiary"}
                aria-label={nextSlideTitle}
                w={10}
                h={10}
                disabled={isNextDisabled}
                data-testid="carousel-next-button"
            >
                <ChevronRightIcon />
            </IconButton>
        </Flex>
    );
};

Carousel.displayName = "Carousel";
